// ===================================================================
// PA GRANTS — Disasters (FEMA Disaster Declarations)
// Full CRUD with search, create/edit modal, delete confirmation
// ===================================================================

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Textarea,
  Select, ActionIcon, Modal, Stack, SimpleGrid, Table, Loader, Center,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh, IconDownload,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { exportToCSV } from '../../lib/exportUtils';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import type { PADisaster } from '../../types/pagrants';

// ── Constants ────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'monitoring', label: 'Monitoring' },
  { value: 'closeout', label: 'Closeout' },
  { value: 'closed', label: 'Closed' },
];

const STATUS_COLORS: Record<string, string> = {
  active: 'green', monitoring: 'yellow', closeout: 'orange', closed: 'gray',
};

const DECLARATION_TYPE_OPTIONS = [
  { value: 'major', label: 'Major Disaster' },
  { value: 'emergency', label: 'Emergency' },
  { value: 'fire_management', label: 'Fire Management' },
];

const DECLARATION_COLORS: Record<string, string> = {
  major: 'red', emergency: 'orange', fire_management: 'yellow',
};

const EMPTY: Partial<PADisaster> = {
  disasterNumber: '', disasterName: '', declarationType: 'major',
  incidentType: '', declarationDate: '', state: '',
  incidentPeriodStart: '', incidentPeriodEnd: '',
  designatedCounties: '', paDesignatedAreas: '',
  federalSharePct: '75', largeProjectThreshold: '', smallProjectMinimum: '',
  perCapitaIndicator: '', countyPerCapita: '',
  emergencyWorkDeadline: '', permanentWorkDeadline: '',
  programDeliveryManagerName: '', programDeliveryManagerEmail: '',
  programDeliveryManagerPhone: '', femaRegion: '', cfda: '',
  status: 'active', notes: '',
};

// ── Component ────────────────────────────────────────────────

export default function Disasters() {
  const { canEdit } = useModulePermission('pagrants');
  const {
    disasters, loadingDisasters,
    fetchDisasters, createDisaster, updateDisaster, deleteDisaster,
  } = usePAGrantsStore();

  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<PADisaster | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PADisaster | null>(null);
  const [form, setForm] = useState<Partial<PADisaster>>({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchDisasters(); }, [fetchDisasters]);

  const filtered = useMemo(() => {
    let list = disasters;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(d =>
        d.disasterNumber.toLowerCase().includes(q) ||
        d.disasterName.toLowerCase().includes(q) ||
        d.incidentType.toLowerCase().includes(q) ||
        d.state.toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) => (a.disasterNumber || '').localeCompare(b.disasterNumber || ''));
  }, [disasters, search]);

  const setField = (key: keyof PADisaster, value: string) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => { setEditTarget(null); setForm({ ...EMPTY }); setModalOpen(true); };
  const openEdit = (d: PADisaster) => { setEditTarget(d); setForm({ ...d }); setModalOpen(true); };

  const handleSave = async () => {
    if (!form.disasterName?.trim()) {
      notifications.show({ message: 'Disaster name is required', color: 'yellow' });
      return;
    }
    setSaving(true);
    try {
      if (editTarget) {
        await updateDisaster(editTarget.id, form);
        notifications.show({ message: 'Disaster updated', color: 'green' });
      } else {
        await createDisaster(form);
        notifications.show({ message: 'Disaster created', color: 'green' });
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteDisaster(deleteTarget.id);
      notifications.show({ message: `${deleteTarget.disasterName} deleted`, color: 'gray' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'red' });
    } finally { setSaving(false); }
  };

  if (loadingDisasters && disasters.length === 0) {
    return <Center h={300}><Loader size="lg" /></Center>;
  }

  const declLabel = (v: string) => DECLARATION_TYPE_OPTIONS.find(o => o.value === v)?.label ?? v;

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Disasters</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconDownload size={16} />} onClick={() => {
            exportToCSV('pa-disasters.csv',
              ['Disaster #', 'Name', 'Declaration Type', 'Declaration Date', 'State', 'Status'],
              filtered.map(d => [d.disasterNumber, d.disasterName, d.declarationType, d.declarationDate, d.state, d.status])
            );
          }}>Export CSV</Button>
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingDisasters} onClick={() => fetchDisasters()}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Disaster</Button>}
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search by name, number, type, state..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.target.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} disaster{filtered.length !== 1 ? 's' : ''}</Text>

      {filtered.length === 0 ? (
        <Paper p="md" radius="md"><Text c="dimmed">No disasters found.</Text></Paper>
      ) : (
        <Paper withBorder>
          <div style={{ overflowX: 'auto' }}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Disaster #</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Declaration</Table.Th>
                  <Table.Th>Incident Type</Table.Th>
                  <Table.Th>State</Table.Th>
                  <Table.Th>Federal %</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Incident Start</Table.Th>
                  <Table.Th>Incident End</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filtered.map(d => (
                  <Table.Tr key={d.id}>
                    <Table.Td>{d.disasterNumber || '\u2014'}</Table.Td>
                    <Table.Td fw={500}>{d.disasterName || '\u2014'}</Table.Td>
                    <Table.Td>
                      <Badge size="sm" color={DECLARATION_COLORS[d.declarationType] ?? 'gray'}>
                        {declLabel(d.declarationType)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{d.incidentType || '\u2014'}</Table.Td>
                    <Table.Td>{d.state || '\u2014'}</Table.Td>
                    <Table.Td>{d.federalSharePct ? `${d.federalSharePct}%` : '\u2014'}</Table.Td>
                    <Table.Td>
                      <Badge size="sm" color={STATUS_COLORS[d.status] ?? 'gray'}>{d.status}</Badge>
                    </Table.Td>
                    <Table.Td>{d.incidentPeriodStart || '\u2014'}</Table.Td>
                    <Table.Td>{d.incidentPeriodEnd || '\u2014'}</Table.Td>
                    <Table.Td>
                      <Group gap={4} justify="flex-end">
                        {canEdit && (
                          <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(d)}>
                            <IconPencil size={14} />
                          </ActionIcon>
                        )}
                        {canEdit && (
                          <ActionIcon size="sm" variant="subtle" color="red" onClick={() => setDeleteTarget(d)}>
                            <IconTrash size={14} />
                          </ActionIcon>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </div>
        </Paper>
      )}

      {/* ── Create / Edit Modal ──────────────────────────────── */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Disaster' : 'Add Disaster'} centered size="xl">
        <Stack>
          {/* Declaration Info */}
          <Paper withBorder p="sm" radius="sm">
            <Text size="sm" fw={600} c="dimmed" mb="xs">Declaration Info</Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              <TextInput label="Disaster Number" value={form.disasterNumber ?? ''}
                onChange={e => setField('disasterNumber', e.target.value)} />
              <TextInput label="Disaster Name" required value={form.disasterName ?? ''}
                onChange={e => setField('disasterName', e.target.value)} />
              <Select label="Declaration Type" data={DECLARATION_TYPE_OPTIONS}
                value={form.declarationType ?? 'major'}
                onChange={v => setField('declarationType', v ?? 'major')} />
              <TextInput label="Incident Type" value={form.incidentType ?? ''}
                onChange={e => setField('incidentType', e.target.value)} />
              <TextInput label="Declaration Date" type="date" value={form.declarationDate ?? ''}
                onChange={e => setField('declarationDate', e.target.value)} />
              <TextInput label="State" value={form.state ?? ''}
                onChange={e => setField('state', e.target.value)} />
            </SimpleGrid>
          </Paper>

          {/* Incident Period */}
          <Paper withBorder p="sm" radius="sm">
            <Text size="sm" fw={600} c="dimmed" mb="xs">Incident Period</Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              <TextInput label="Incident Period Start" type="date" value={form.incidentPeriodStart ?? ''}
                onChange={e => setField('incidentPeriodStart', e.target.value)} />
              <TextInput label="Incident Period End" type="date" value={form.incidentPeriodEnd ?? ''}
                onChange={e => setField('incidentPeriodEnd', e.target.value)} />
            </SimpleGrid>
          </Paper>

          {/* Designated Areas */}
          <Paper withBorder p="sm" radius="sm">
            <Text size="sm" fw={600} c="dimmed" mb="xs">Designated Areas</Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              <Textarea label="Designated Counties" value={form.designatedCounties ?? ''}
                onChange={e => setField('designatedCounties', e.target.value)} autosize minRows={2} />
              <Textarea label="PA Designated Areas" value={form.paDesignatedAreas ?? ''}
                onChange={e => setField('paDesignatedAreas', e.target.value)} autosize minRows={2} />
            </SimpleGrid>
          </Paper>

          {/* Financial Thresholds */}
          <Paper withBorder p="sm" radius="sm">
            <Text size="sm" fw={600} c="dimmed" mb="xs">Financial Thresholds</Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              <TextInput label="Federal Share %" value={form.federalSharePct ?? '75'}
                onChange={e => setField('federalSharePct', e.target.value)} />
              <TextInput label="Large Project Threshold" value={form.largeProjectThreshold ?? ''}
                onChange={e => setField('largeProjectThreshold', e.target.value)} />
              <TextInput label="Small Project Minimum" value={form.smallProjectMinimum ?? ''}
                onChange={e => setField('smallProjectMinimum', e.target.value)} />
              <TextInput label="Per Capita Indicator" value={form.perCapitaIndicator ?? ''}
                onChange={e => setField('perCapitaIndicator', e.target.value)} />
              <TextInput label="County Per Capita" value={form.countyPerCapita ?? ''}
                onChange={e => setField('countyPerCapita', e.target.value)} />
            </SimpleGrid>
          </Paper>

          {/* Deadlines */}
          <Paper withBorder p="sm" radius="sm">
            <Text size="sm" fw={600} c="dimmed" mb="xs">Deadlines</Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              <TextInput label="Emergency Work Deadline" type="date" value={form.emergencyWorkDeadline ?? ''}
                onChange={e => setField('emergencyWorkDeadline', e.target.value)} />
              <TextInput label="Permanent Work Deadline" type="date" value={form.permanentWorkDeadline ?? ''}
                onChange={e => setField('permanentWorkDeadline', e.target.value)} />
            </SimpleGrid>
          </Paper>

          {/* FEMA Contacts */}
          <Paper withBorder p="sm" radius="sm">
            <Text size="sm" fw={600} c="dimmed" mb="xs">FEMA Contacts</Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              <TextInput label="PDM Name" value={form.programDeliveryManagerName ?? ''}
                onChange={e => setField('programDeliveryManagerName', e.target.value)} />
              <TextInput label="PDM Email" value={form.programDeliveryManagerEmail ?? ''}
                onChange={e => setField('programDeliveryManagerEmail', e.target.value)} />
              <TextInput label="PDM Phone" value={form.programDeliveryManagerPhone ?? ''}
                onChange={e => setField('programDeliveryManagerPhone', e.target.value)} />
              <TextInput label="FEMA Region" value={form.femaRegion ?? ''}
                onChange={e => setField('femaRegion', e.target.value)} />
              <TextInput label="CFDA" value={form.cfda ?? ''}
                onChange={e => setField('cfda', e.target.value)} />
            </SimpleGrid>
          </Paper>

          {/* Status & Notes */}
          <Paper withBorder p="sm" radius="sm">
            <Text size="sm" fw={600} c="dimmed" mb="xs">Status</Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              <Select label="Status" data={STATUS_OPTIONS}
                value={form.status ?? 'active'}
                onChange={v => setField('status', v ?? 'active')} />
            </SimpleGrid>
            <Textarea label="Notes" mt="sm" value={form.notes ?? ''}
              onChange={e => setField('notes', e.target.value)} autosize minRows={2} />
          </Paper>

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            {canEdit && (
              <Button loading={saving} onClick={handleSave}>
                {editTarget ? 'Save Changes' : 'Create Disaster'}
              </Button>
            )}
          </Group>
        </Stack>
      </Modal>

      {/* ── Delete Confirmation ──────────────────────────────── */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)}
        title="Delete Disaster" centered size="sm">
        <Stack>
          <Text size="sm">
            Delete <strong>{deleteTarget?.disasterName}</strong> ({deleteTarget?.disasterNumber})? This cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            {canEdit && <Button color="red" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>}
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
